<template>
  <list-page-shell>
    <template #actions>
      <el-button size="small" icon="el-icon-arrow-left" @click="prevMonth">{{ $t('schedulePage.prev') }}</el-button>
      <el-button size="small" @click="goToday">{{ $t('common.today') }}</el-button>
      <el-button size="small" icon="el-icon-arrow-right" @click="nextMonth">{{ $t('schedulePage.next') }}</el-button>
    </template>

    <div class="schedule-grid">
      <el-card shadow="never">
        <div slot="header" class="hdr">
          <div>
            <div class="title">{{ $t('schedulePage.calendarPanel') }}</div>
            <div v-if="timezone" class="sub">{{ timezone }}</div>
          </div>
        </div>
        <p v-if="hint" class="hint hint--warn">{{ hint }}</p>
        <p v-if="selfGoogleHint" class="hint">
          {{ selfGoogleHint }}
          <router-link class="hint-link" to="/home">{{ $t('schedulePage.connectGoogle') }}</router-link>
        </p>
        <p v-if="officePendingHint" class="hint hint--info">{{ officePendingHint }}</p>
        <el-calendar v-model="calMonth" v-loading="loading">
          <template slot="dateCell" slot-scope="{ data }">
            <div class="cal-cell">
              <span class="day-num">{{ data.day.split('-').slice(2).join('-') }}</span>
              <div v-for="(ev, idx) in eventsByDay[data.day]" :key="idx" class="ev-pill" @click.stop="open(ev)">
                {{ ev.summary || $t('schedulePage.noEventTitle') }}
              </div>
            </div>
          </template>
        </el-calendar>
      </el-card>

      <el-card shadow="never">
        <div slot="header">{{ $t('schedulePage.monthEvents') }}</div>
        <el-table :data="flatEvents" size="small" border :empty-text="$t('schedulePage.emptyEventsMonth')">
          <el-table-column :label="$t('schedulePage.colEventStart')" width="170">
            <template slot-scope="scope">{{ formatEventTime(scope.row.start) || '—' }}</template>
          </el-table-column>
          <el-table-column :label="$t('schedulePage.colEventEnd')" width="170">
            <template slot-scope="scope">{{ formatEventTime(scope.row.end) || '—' }}</template>
          </el-table-column>
          <el-table-column prop="summary" :label="$t('schedulePage.colEventTitle')" min-width="160" show-overflow-tooltip />
          <el-table-column prop="assignedSalesUserName" :label="$t('schedulePage.colAssignedSales')" width="110" show-overflow-tooltip />
          <el-table-column :label="$t('projectDetailPage.attachmentCol')" width="80">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="open(scope.row)">{{ $t('schedulePage.openLead') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import { listScheduleVisitEvents } from '@/api/schedule'

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function formatEventDateTime(value, timeZone) {
  if (!value) return ''
  const raw = String(value).trim()
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnly) {
    const d = new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3])
    return new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    }).format(d)
  }

  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone || undefined
  }).format(d)
}

export default {
  name: 'ScheduleCalendar',
  components: { ListPageShell },
  data() {
    return {
      loading: false,
      calMonth: new Date(),
      events: [],
      timezone: '',
      hint: '',
      selfGoogleHint: '',
      officePendingHint: '',
      googleConnected: false,
      officeViewer: false
    }
  },
  computed: {
    range() {
      const y = this.calMonth.getFullYear()
      const m = this.calMonth.getMonth()
      const start = new Date(y, m, 1)
      const end = new Date(y, m + 1, 0)
      return { start: toYmd(start), end: toYmd(end) }
    },
    eventsByDay() {
      const map = {}
      for (const ev of this.events) {
        const day = (ev.start || '').slice(0, 10)
        if (!day || day.length < 10) continue
        if (!map[day]) map[day] = []
        map[day].push(ev)
      }
      return map
    },
    flatEvents() {
      return [...this.events].sort((a, b) => String(a.start).localeCompare(String(b.start)))
    }
  },
  watch: {
    calMonth() {
      this.load()
    }
  },
  created() {
    this.load()
  },
  methods: {
    prevMonth() {
      const d = new Date(this.calMonth)
      d.setMonth(d.getMonth() - 1)
      this.calMonth = d
    },
    nextMonth() {
      const d = new Date(this.calMonth)
      d.setMonth(d.getMonth() + 1)
      this.calMonth = d
    },
    goToday() {
      this.calMonth = new Date()
    },
    open(ev) {
      if (ev && ev.leadId) {
        this.$router.push({ path: `/lead/detail/${ev.leadId}` })
      }
    },
    formatEventTime(value) {
      return formatEventDateTime(value, this.timezone)
    },
    buildOfficePendingHint(list) {
      if (!list || !list.length) return ''
      const names = list.map(u => u.realName || u.userId).filter(Boolean).join('、')
      return this.$t('schedulePage.hintOfficePendingGoogle', { names })
    },
    async load() {
      this.loading = true
      this.hint = ''
      this.selfGoogleHint = ''
      this.officePendingHint = ''
      try {
        const raw = await listScheduleVisitEvents({ startDate: this.range.start, endDate: this.range.end })
        if (raw.code && raw.code !== 200) {
          this.events = []
          this.timezone = ''
          this.hint = raw.msg || this.$t('schedulePage.loadFailed')
          return
        }
        const d = raw.data !== undefined ? raw.data : raw
        this.events = (d && d.events) || []
        this.timezone = (d && d.timezone) || ''
        this.googleConnected = !!(d && d.googleConnected)
        this.officeViewer = !!(d && d.officeViewer)
        if (!this.googleConnected) {
          this.selfGoogleHint = this.$t('schedulePage.hintSelfGoogle')
        }
        if (this.officeViewer) {
          this.officePendingHint = this.buildOfficePendingHint(d && d.officeUsersWithoutGoogle)
        }
      } catch (e) {
        this.events = []
        this.hint = (e && e.message) || this.$t('schedulePage.loadFailed')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.schedule-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}

.hdr {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
}
.title {
  font-weight: 600;
  font-size: 16px;
  margin-right: 8px;
  color: var(--color-text);
}
.sub {
  font-size: 12px;
  color: var(--dark-text-sub, #8b95a1);
}
.hint {
  color: #606266;
  font-size: 13px;
  margin: 0 0 12px;
  line-height: 1.5;
}
.hint--warn {
  color: #e6a23c;
}
.hint--info {
  color: #409eff;
}
.hint-link {
  margin-left: 6px;
  color: #2563eb;
  text-decoration: none;
}
.hint-link:hover {
  text-decoration: underline;
}
.cal-cell {
  min-height: 72px;
  padding: 2px 0;
}
.day-num {
  font-size: 12px;
  color: var(--color-text-sub);
}
.ev-pill {
  font-size: 11px;
  margin-top: 2px;
  padding: 2px 4px;
  background: rgba(96, 165, 250, 0.12);
  color: #60a5fa;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

@media (max-width: 1100px) {
  .schedule-grid {
    grid-template-columns: 1fr;
  }
}
</style>
